import { useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LuHouse, LuGlobe, LuMessageCircle, LuUser } from "react-icons/lu";
import useKeyboardOpen from "../hooks/useKeyboardOpen";
import useUnreadMessages from "../hooks/useUnreadMessages";
import { useUser } from "../contexts/UserContext";
import { useRecord } from "../utils/appData";
import "./MobileBottomNav.css";

const TABS = [
  {
    label: "Home",
    to: "/dashboard",
    Icon: LuHouse,
    paths: ["/dashboard", "/jobs", "/post-job", "/funds"],
  },
  {
    label: "Discover",
    to: "/discover",
    Icon: LuGlobe,
    paths: ["/discover", "/user-profile"],
  },
  {
    label: "Messages",
    to: "/chats",
    Icon: LuMessageCircle,
    paths: ["/chats", "/chat", "/group-chat", "/create-group"],
  },
  {
    label: "Profile",
    to: "/profile",
    Icon: LuUser,
    paths: [
      "/profile",
      "/settings",
      "/edit-profile",
      "/bank-payouts",
      "/my-applications",
    ],
  },
];
export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const keyboard = useKeyboardOpen();
  const unread = useUnreadMessages();
  const { currentUser } = useUser();
  const { data: requests } = useRecord(
    currentUser?.uid ? `Users/${currentUser.uid}/friendRequests` : null,
  );
  const activity = Object.values(requests || {}).filter(
    (value) => value === "received",
  ).length;
  const activeIndex = TABS.findIndex((tab) =>
    tab.paths.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    ),
  );
  const islandRef = useRef(null);
  const slotsRef = useRef([]);
  const contentRef = useRef([]);
  const measuredRef = useRef(false);
  const [pill, setPill] = useState({
    left: 0,
    width: 0,
    ready: false,
    animate: false,
  });
  useLayoutEffect(() => {
    if (keyboard || activeIndex < 0) return;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      const island = islandRef.current?.getBoundingClientRect();
      const slot = slotsRef.current[activeIndex]?.getBoundingClientRect();
      const content = contentRef.current[activeIndex]?.getBoundingClientRect();
      if (!island?.width || !slot || !content?.width) return;
      const width = Math.min(content.width + 32, island.width - 2);
      const left = Math.max(
        1,
        Math.min(
          island.width - width - 1,
          slot.left - island.left + slot.width / 2 - width / 2,
        ),
      );
      const animate = measuredRef.current;
      measuredRef.current = true;
      setPill((previous) =>
        previous.ready &&
        Math.abs(previous.left - left) < 0.25 &&
        Math.abs(previous.width - width) < 0.25
          ? previous
          : { left, width, ready: true, animate },
      );
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (islandRef.current) observer?.observe(islandRef.current);
    if (contentRef.current[activeIndex])
      observer?.observe(contentRef.current[activeIndex]);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [activeIndex, keyboard]);
  if (keyboard || pathname === "/post-job") return null;
  return (
    <div className="zy-mobile-nav-wrap">
      <nav
        className="zy-mobile-nav-island"
        aria-label="Mobile navigation"
        ref={islandRef}
      >
        <span
          className="zy-mobile-nav-pill"
          aria-hidden="true"
          data-animate={pill.animate ? "true" : undefined}
          style={{
            width: pill.width,
            transform: `translateX(${pill.left}px)`,
            opacity: pill.ready && activeIndex >= 0 ? 1 : 0,
          }}
        />
        {TABS.map(({ label, to, Icon }, index) => {
          const active = index === activeIndex;
          const badge =
            label === "Messages" ? unread : label === "Home" ? activity : 0;
          return (
            <Link
              key={label}
              to={to}
              ref={(node) => {
                slotsRef.current[index] = node;
              }}
              className="zy-mobile-nav-item"
              aria-label={label}
              aria-current={active ? "page" : undefined}
            >
              <span
                className="zy-mobile-nav-content"
                ref={(node) => {
                  contentRef.current[index] = node;
                }}
              >
                <Icon
                  size={20}
                  strokeWidth={active ? 2.5 : 2}
                  aria-hidden="true"
                />
                {active && <span className="zy-mobile-nav-label">{label}</span>}
                {badge > 0 && !active && (
                  <span
                    className="zy-mobile-nav-badge"
                    aria-label={`${badge} ${label === "Messages" ? "unread messages" : "friend requests"}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
